//admin view of project detail page
import { useState, useEffect, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import PulseLoader from "react-spinners/PulseLoader";

import { Card, Badge } from "@mui/material";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import CardMedia from "@mui/material/CardMedia";
import TableRowsIcon from "@mui/icons-material/TableRows";
import GridViewIcon from "@mui/icons-material/GridView";
import IconButton from "@mui/material/IconButton";
import ToggleButton from "@mui/material/ToggleButton";

import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import EditIcon from "@mui/icons-material/Edit";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PrivacyTipIcon from "@mui/icons-material/PrivacyTip";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiAvatar from "components/SuiAvatar";
import SuiBadge from "components/SuiBadge";
import ConversationList from "components/UpStyled/Conversations";
import ReadMore from "components/UpStyled/ReadMore";

import toast from "react-hot-toast";
import moment from "moment";
import { capitalCase } from "capital-case";
import { useForm, Controller } from "react-hook-form";

import api from "services/api";
import ChatRoom from "pages/messages/chatroom";
import { AccountContext } from "context/account";
import CategoryContext from "context/category-context";
import ProjectStyles from "pages/de/project/detail/components/ProjectStyles";
import ProjectInfo from "pages/de/project/detail/components/ProjectInfo";
import ResponseStatus from "pages/admin/projects/detail/components/ResponseStatus";

import ContactAvatar from "components/UpStyled/ContactAvatar";
import { MAX_SELECTED_COUNT } from "utils/params";
import ProjectStatusTag from "components/UpStyled/ProjectStatusTag";
import { filterParser } from "utils/helper";
import Prompt from "components/UpStyled/Prompt";
import qs from "query-string";

import Error404 from "pages/error/404";

const ProjectDetail = ({ project, handleRefresh = () => {} }) => {
  const [providerChatroomId, setProviderChatroomId] = useState(null);
  const [chatrooms, setChatRooms] = useState(null);
  const [selectedChatroomId, setSelectedChatroomId] = useState(null);
  const [selectedChatroom, setSelectedChatroom] = useState(null);
  const [provider, setProvider] = useState({});
  const [tabIndex, setTabIndex] = useState(0);
  const [categoryLabel, setCategoryLabel] = useState(""); //Using Category Name if the category ID was found at category tree
  const [unReadCount, setUnReadCount] = useState(0);
  const [privateVisible, setPrivateVisible] = useState(false);
  const [isPublic, setIsPublic] = useState(project?.visibility == "private" ? false : true);
  const [owner, setOwner] = useState({});

  const ownerStore = owner?.stores && owner.stores.length > 0 ? owner.stores[0] : {};

  useEffect(() => {
    if (project.provider_id) {
      api.account
        .get(project.provider_id)
        .then(({ data }) => {
          const stores = data?.user?.stores || [];
          if (stores.length > 0) {
            setProvider(stores[0]);
          }
        })
        .catch((err) => console.log(err));
    }
    if (project.owner_id) {
      api.users
        .retrieve(project.owner_id)
        .then(({ data }) => setOwner(data?.user || {}))
        .catch((err) => console.log(err));
    }
  }, [project]);

  return project.id ? (
    <div>
      <SuiBox display="flex" alignItems="center" justifyContent="space-between">
        <SuiBox>
          <SuiTypography variant="h6" fontWeight="medium" textTransform="capitalize">
            Project Details
          </SuiTypography>
        </SuiBox>
      </SuiBox>

      <Card sx={{ p: 3, my: 2 }}>
        <SuiBox
          display="flex"
          justifyContent="space-between"
          sx={{ flex: "flex-wrap" }}
          alignItems="flex-start"
          gap={2}
        >
          <SuiBox display="flex" justifyContent="flex-start" alignItems="center" py={0.5}>
            <SuiBox>
              <Tooltip title={project.name}>
                <SuiBox>
                  <SuiTypography
                    variant="h6"
                    fontWeight="medium"
                    textTransform="capitalize"
                    lineHeight="1.2"
                    sx={{
                      width: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: "1",
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {project.name}
                  </SuiTypography>
                </SuiBox>
              </Tooltip>
            </SuiBox>

            <SuiBox>
              {" "}
              <ProjectStatusTag status={project.status} />
            </SuiBox>
          </SuiBox>
        </SuiBox>
        <SuiBox display="flex" alignItems="center" justifyContent="flex-start" gap={1}>
          <SuiTypography variant="body1" textTransform="capitalize" color="text">
            Designer:{" "}
          </SuiTypography>
          {ownerStore?.name ? (
            <SuiBox display="flex" justifyContent="flex-start" alignItems="center" gap={1} mb={0.5}>
              <SuiBox component={Link} to={`/wall/portfolio/${owner.id}`}>
                <Tooltip position="top" title={ownerStore?.name}>
                  <SuiAvatar
                    src={ownerStore?.metadata?.avatar}
                    alt="profile-image"
                    variant="circle"
                    size="xs"
                    shadow="sm"
                  />
                </Tooltip>
              </SuiBox>
              <SuiBox component={Link} to={`/wall/portfolio/${owner.id}`}>
                <SuiTypography variant="button" fontWeight="regular" textTransform="capitalize">
                  {ownerStore?.name}
                </SuiTypography>
              </SuiBox>
            </SuiBox>
          ) : (
            "N/A"
          )}
        </SuiBox>
        <SuiBox
          display="flex"
          sx={{ flexWrap: "wrap" }}
          alignItems="flex-start"
          justifyContent="flex-start"
          gap={1}
        >
          <SuiTypography variant="button" fontWeight="regular" color="text">
            Contact:
          </SuiTypography>
          <SuiBox display="flex" alignItems="flex-start" justifyContent="flex-start" sx={{ mr: 2 }}>
            <SuiBox>
              <EmailIcon fontSize="small" />
            </SuiBox>
            <SuiTypography variant="button" fontWeight="regular" color="text">
              {owner.email}
            </SuiTypography>
          </SuiBox>
          <SuiBox display="flex" alignItems="flex-start" justifyContent="flex-start">
            <SuiBox>
              <PhoneIcon fontSize="small" />
            </SuiBox>
            <SuiTypography variant="button" fontWeight="regular" color="text">
              {project?.metadata?.business_phone || ownerStore?.metadata?.business_phone}
            </SuiTypography>
          </SuiBox>
        </SuiBox>
        <SuiBox display="flex" gap={1}>
          <SuiTypography variant="body1" textTransform="capitalize" color="text">
            Visibility:
          </SuiTypography>
          <SuiTypography variant="body1" textTransform="capitalize" color="text">
            {project?.visibility ? project?.visibility : "N/A"}
          </SuiTypography>
        </SuiBox>
        {project.provider_id ? (
          <SuiBox
            key={"Service Provider"}
            display="flex"
            alignItems="center"
            py={1}
            pr={2}
            className="gap-1 items-start"
          >
            <SuiTypography
              variant="button"
              fontWeight="bold"
              textTransform="capitalize"
              sx={{ whiteSpace: "nowrap" }}
            >
              Service Provider:
            </SuiTypography>

            <SuiBox display="flex" justifyContent="flex-start" alignItems="center" gap={1}>
              <SuiBox component={Link} to={`/wall/portfolio/${project.provider_id}`}>
                <Tooltip position="top" title={provider.name}>
                  <SuiAvatar
                    src={provider?.metadata?.avatar}
                    alt="profile-image"
                    variant="circle"
                    size="xs"
                    shadow="sm"
                  />
                </Tooltip>
              </SuiBox>
              <SuiBox component={Link} to={`/wall/portfolio/${project.provider_id}`}>
                <SuiTypography variant="button" fontWeight="regular" textTransform="capitalize">
                  {provider.name}
                </SuiTypography>
              </SuiBox>
            </SuiBox>
          </SuiBox>
        ) : null}
      </Card>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12} md={12} lg={providerChatroomId || chatrooms ? 6 : 12}>
          {project?.products && project.products.length > 0 ? (
            <ProjectStyles project={project} />
          ) : (
            <ProjectInfo project={project} showResponseStatus={true} />
          )}
        </Grid>
      </Grid>
      <SuiBox mt={2}>
        {project?.products && project.products.length > 0 ? (
          <ProjectInfo project={project} showResponseStatus={true} />
        ) : null}
      </SuiBox>

      <SuiBox mt={2}>
        <ResponseStatus project={project} />
      </SuiBox>
      {privateVisible ? (
        <Prompt
          heading="Project Visibility"
          text={
            <SuiBox>
              <SuiBox>
                Are you sure to change your project visibility to{" "}
                {isPublic == true ? "Private" : "Public"}.{" "}
              </SuiBox>
              <SuiBox>
                {isPublic == true &&
                (!project?.audiences || (project?.audiences && project?.audiences.length == 0))
                  ? "Your project will be invisible to manufacturers before you select any manufacturer to send request."
                  : ""}
              </SuiBox>
            </SuiBox>
          }
          visible={privateVisible}
          okText="Update"
          okColor="dark"
          handleOk={() => {
            const visibilityTo = isPublic == true ? "private" : "public";
            const payload = {
              visibility: visibilityTo,
            };
            api.project
              .update(project.id, payload)
              .then(() => {
                toast.success(`Change project visibility to project ${visibilityTo} successfully!`);
                setIsPublic(!isPublic);
                setPrivateVisible(false);
              })
              .catch((err) => {
                console.log(err);
                toast.error(`Change project visibility to project ${visibilityTo} failed!`);
              });
          }}
          handleClose={() => setPrivateVisible(false)}
        />
      ) : null}
    </div>
  ) : null;
};

export default ProjectDetail;
